import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import color from "color";
import { useIntl } from "react-intl";

import Color from "@mapmycustomers/shared/enum/Color";
import { Group } from "@mapmycustomers/shared/types/entity";
import { Button, LoadingSpinner } from "@mapmycustomers/ui";

import ButtonLink from "@app/component/ButtonLink";
import Interactive from "@app/component/map/FeaturedMap/essentials/controls/Interactive";
import { applyGroupFilters, exitGroupMode } from "@app/scene/map/store/actions";
import { getGroup } from "@app/scene/map/store/selectors";
import { RootState } from "@app/store/rootReducer";
import colors from "@app/styles/_colors.module.scss";
import anyColorToHex from "@app/util/colors/anyColorToHex";
import getContrastColor from "@app/util/colors/getContrastColor";
import useAnalytics from "@app/util/contexts/useAnalytics";

import styles from "./Banner.module.scss";

interface Props {
  applyGroupFilters: typeof applyGroupFilters;
  group?: Group;
  onExitGroupMode?: () => void;
}

const Banner: React.FC<Props> = ({ applyGroupFilters, group, onExitGroupMode }) => {
  const analytics = useAnalytics();
  const intl = useIntl();

  const [backgroundColor, textColor] = useMemo(() => {
    if (!group) {
      return ["transparent", "transparent"]; // not used anyway as banner is hidden when there's no group
    }

    const background = color(anyColorToHex(group.color || Color.BLACK))
      .lightness(80)
      .hex();
    const textColor = getContrastColor(background) === "white" ? colors.white : colors.slate;
    return [background, textColor];
  }, [group]);

  const handleExit = useCallback(() => {
    onExitGroupMode?.();
    analytics.completed("");
  }, [analytics, onExitGroupMode]);

  const handleSwitchGroupFilter = useCallback(() => {
    applyGroupFilters();
    analytics.cancelled("");
  }, [analytics, applyGroupFilters]);

  return group ? (
    <div className={styles.container} style={{ backgroundColor }}>
      <div className={styles.title} style={{ color: textColor }}>
        {intl.formatMessage(
          {
            id: "map.groups.topPanel.banner.title",
            defaultMessage: "You are viewing the Group {groupName}",
            description: "Group mode banner's title on the Map page",
          },
          {
            groupName: group ? (
              intl.formatMessage(
                {
                  id: "map.groups.topPanel.banner.title.name",
                  defaultMessage: "“{groupName}”",
                  description: "Group name in group mode banner's title on the Map page",
                },
                { groupName: group.name }
              )
            ) : (
              <LoadingSpinner className={styles.spinner} mini />
            ),
          }
        )}

        {group && (
          <Interactive>
            <ButtonLink onClick={handleSwitchGroupFilter}>
              {intl.formatMessage({
                id: "map.groups.topPanel.banner.title.filter",
                defaultMessage: "Filter by Groups Instead",
                description: "Title of invitation to switch to filtering by group on the Map page",
              })}
            </ButtonLink>
          </Interactive>
        )}
      </div>
      <div className={styles.actions}>
        <Interactive>
          <Button onClick={handleExit} type="default">
            {intl.formatMessage({
              id: "map.groups.topPanel.banner.exit",
              defaultMessage: "Exit Group View",
              description: "Group mode banner's exit button on the Map page",
            })}
          </Button>
        </Interactive>
      </div>
    </div>
  ) : null;
};

const mapStateToProps = (state: RootState) => ({
  group: getGroup(state),
});

const mapDispatchToProps = {
  applyGroupFilters,
  onExitGroupMode: exitGroupMode.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(Banner);
